/**
 * HOW HIGH A POPUP THAT ESCAPES TO THE BODY HAS TO SIT.
 *
 * A menu, a picker or a popover anchored to a trigger is portalled to `document.body` so it is
 * not clipped by the scrolling, overflow-hidden box it belongs to. That escape has a price: its
 * stacking is then settled against the WHOLE PAGE, not against the thing that opened it. A
 * number that is obviously high enough beside its own trigger can be far too low once the popup
 * is a child of `<body>`.
 *
 * Which is how this was found. Every one of these sat at `z-50`, and on a phone a block's editor
 * is a full-screen `z-[100]` sheet — so a menu opened from inside that editor rendered BEHIND
 * it. In the DOM, correctly positioned, correctly sized, and under an opaque surface. Tapping a
 * reference chip on a phone looked like nothing happening at all, and the automated checks all
 * passed, because "does it exist" and "where is its rect" are both true of a popup nobody can
 * see.
 *
 * ## One level, not a number per popup
 *
 * There is no useful ordering AMONG these. Two trigger-anchored popups cannot be open at once —
 * each closes on an outside press — so the only relationship that matters is the one they all
 * share with the page: above everything. Giving each its own number means each is a separate
 * judgement about a global question, made by someone looking at one component, and `z-50`
 * repeated seven times is what that produces.
 *
 * So the rule is: above the tallest surface in the app, and `popup-level.test.ts` is what keeps
 * it true. Add a taller surface and that test fails and names it, rather than a popup going
 * quietly invisible on one device.
 */
export const POPUP_LEVEL = 'z-[140]';

/**
 * The click-catcher some popups lay over the page, directly beneath the popup itself.
 *
 * Paired with `POPUP_LEVEL` on purpose: the gap between them is the whole contract — the sheet
 * swallows a press meant for the page while never covering the menu it belongs to. Written as
 * its own constant so raising one cannot silently leave the other behind.
 */
export const POPUP_BACKDROP_LEVEL = 'z-[139]';
